import crypto from 'crypto';
import Cookies from 'js-cookie';
import {v4 as uuidv4} from 'uuid';
import {fingerprintStore, UnityConfig} from './fingerprints/FingerprintStore';
import {CSRF_HEADER, INSTALL_ID_COOKIE_NAME} from './constant';
import getGameId from '@/utils/getGameId';
import {getCsrfToken} from '@/utils/getCsrfToken';

// The main use of this file is to provide a central place to add
// the core details of the unity instance and the install ID to the headers.
/** Sentinel message for requests fired without a game/unity context (e.g. the
 *  anonymous partner catalogue's background badge/LTO fetches). RequestCoalescer
 *  matches this to log at debug instead of error — these requests are always
 *  `failSilently` and can never succeed off a `/games/{id}` surface. */
export const NO_GAME_CONTEXT_ERROR = 'No game context (gameId=0)';

/**
 * The shell game id, resolved AT RUNTIME. `NEXT_PUBLIC_SKILLZ_APP_SHELL_GAME_ID` is
 * inlined at BUILD time, but on the deployed image that var is set only at runtime (k8s
 * env), so it is `undefined` in the client bundle — which silently disabled the shell
 * reroute below and made every shell-game client call (e.g. the side-menu user/currency
 * fetch) wait 5s for a Unity config that never exists, then return null. `window.__SKILLZ_APP_GAME_ID__`
 * is the same runtime source getGameId() reads (set by the partner/root SDK runtime, only
 * on shell surfaces), so it is the reliable signal on the deployed image; the build-time
 * env stays as a fallback for parity.
 */
function resolveShellGameId(): string | undefined {
  if (typeof window !== 'undefined' && window.__SKILLZ_APP_GAME_ID__) {
    return String(window.__SKILLZ_APP_GAME_ID__);
  }
  return process.env.NEXT_PUBLIC_SKILLZ_APP_SHELL_GAME_ID?.trim() || undefined;
}

/**
 * On the partner arcade (shell game 19369) @sdk-mobile client calls can't be
 * fingerprinted in the browser (server-only fixed token). Reroute them through the
 * same-origin /apigw relay, which resolves the shell fingerprint server-side. The
 * absolute URL from APIUtilities.generateURL is version-prefixed (…/v1/store), so we
 * keep the exact path+query and swap the origin for /apigw — the relay forwards to the
 * identical backend path. Real games (any other gameId) pass through unchanged.
 */
export function rerouteShellUrl(fullURL: string): string {
  const shellGameId = resolveShellGameId();
  if (!shellGameId || getGameId() !== shellGameId) {
    return fullURL;
  }
  try {
    const u = new URL(fullURL);
    return `/apigw${u.pathname}${u.search}`;
  } catch {
    return fullURL;
  }
}

export async function addBrowserSideAppHeaders(headers: Record<string, string>) {
  // Move me to the server side.
  const gameId = getGameId();
  // Off a `/games/{id}` route, getGameId() returns '0' and no unity session
  // exists (the token is minted per-game by the game bootstrap). Fail fast
  // instead of polling waitForUnityConfig for 5s and then throwing — this path
  // is hit repeatedly by the sdk-core BadgeController LTO fetch on the anonymous
  // partner surface.
  //
  if (!gameId || gameId === '0') {
    throw new Error(NO_GAME_CONTEXT_ERROR);
  }

  // The partner arcade resolves getGameId() to the shell game (19369). It has NO
  // client-side unity config (no Unity build; its fingerprint is server-only). So
  // instead of the client fingerprint path below, send only what the /apigw relay
  // reads (game id + install id + CSRF; empty uty-token) and let NetClient reroute
  // the request through /apigw (rerouteShellUrl), which applies the fixed server-held
  // fingerprint via resolveFingerprintToken. Real games are untouched.
  if (gameId === resolveShellGameId()) {
    // Mint the device install id if a call races ahead of PartnerSdkRuntime's
    // hydration (which also ensures it). Guarded on absence, so whichever runs first
    // wins and the rest read the same stable id — no throw, no race.
    let shellInstallId = Cookies.get(INSTALL_ID_COOKIE_NAME);
    if (!shellInstallId) {
      shellInstallId = uuidv4();
      Cookies.set(INSTALL_ID_COOKIE_NAME, shellInstallId, {expires: 3650, path: '/'});
    }
    headers['X-Skillz-Uty-Token'] = '';
    headers['X-Skillz-Install-Id'] = shellInstallId;
    headers['X-Skillz-App-Token-Extended'] = hashSignature(
      Math.floor(Math.random() * 74).toString()
    );
    headers['x-skillz-game-id'] = gameId;
    headers[CSRF_HEADER] = getCsrfToken();
    return headers;
  }

  const unityConfig = await waitForUnityConfig(gameId);

  console.debug('Got unity config:', unityConfig);
  const unityInstanceString = fingerprintStore.getUnityInstanceString();
  const installId = Cookies.get(INSTALL_ID_COOKIE_NAME);

  if (!unityInstanceString) {
    console.debug('Unity instance string not set, this is not an error as this is not in use yet');
  } else {
    headers['X-Skillz-App-Token'] = encodeUnityInstanceString(unityInstanceString);
  }

  if (!installId) {
    console.error('Install ID not set');
    throw new Error('Install ID not set');
  }

  headers['X-Skillz-Uty-Token'] = unityConfig;
  headers['X-Skillz-Install-Id'] = installId;
  headers['X-Skillz-App-Token-Extended'] = hashSignature(Math.floor(Math.random() * 74).toString());
  headers['x-skillz-game-id'] = gameId;
  headers[CSRF_HEADER] = getCsrfToken();
  return headers;
}

function encodeUnityInstanceString(unityConfig: UnityConfig | string): string {
  const jsonString = JSON.stringify(unityConfig);
  return Buffer.from(jsonString).toString('base64');
}

function hashSignature(value: string): string {
  let outValue = value;
  if (outValue.length > 5) {
    outValue = `${value.substring(3)}${value.substring(0, 3)}`;
  }

  const hash = crypto.createHash('sha384');
  hash.update(outValue, 'utf8');
  const digest = hash.digest();

  const ret = cleanString(digest);
  return ret;
}

// Signature Verification Below
function cleanString(data: Buffer): string {
  let ret = data.toString('base64');

  ret = ret.replace(/\+/g, '-');
  ret = ret.replace(/\//g, '_');

  return ret;
}

// This will wait for the Unity config to be set by the iframe before proceeding
// with any backend API calls. We should add monitoring to this flow.
function waitForUnityConfig(gameId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const maxAttempts = 50; // Adjust this value as needed
    let attempts = 0;

    const interval = setInterval(async () => {
      const unityConfig = fingerprintStore.getUnityConfig(gameId);

      if (unityConfig) {
        clearInterval(interval);
        resolve(unityConfig);
      } else {
        attempts++;
        if (attempts >= maxAttempts) {
          console.log('Unity config not set after multiple attempts');
          clearInterval(interval);
          reject(new Error('Unity config not set after multiple attempts'));
        }
      }
    }, 100); // Check every 100ms
  });
}
